import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaDownload, FaSync } from "react-icons/fa";
import * as XLSX from "xlsx";
import {
  SectionHeader,
  SectionTitle,
  SectionActions,
  Button,
  DataTable,
  UserInfo,
  Filters,
  FilterInput,
  DataTableScroll,
  TableHeader,
  TableRow,
  TableCell,
  LoadingCell,
  EmptyCell,
  UserName,
  UserId,
  UserAvatarStyled,
  EmailCell,
  DateCell,
  CourseCountCell,
  SpendingCell,
  PaginationContainer,
  PaginationInfo,
  PaginationControls,
  PaginationButton,
  PageNumbers,
  PageNumber,
} from "./AdminUsers.styles";
import { toast } from "sonner";
import { getAllUsers } from "../../../store/slices/admin/asyncThunks";
import {
  selectUsers,
  selectUsersLoading,
  selectUserFilters,
  selectUsersPagination,
  setUserFilters,
} from "../../../store/slices/admin/slice";
import { priceFormatter } from "../../../utils/utils";
import { useMediaQuery } from "react-responsive";
import adminService from "../../../api/services/admin";

const AdminUsers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const users = useSelector(selectUsers);
  const usersLoading = useSelector(selectUsersLoading);
  const userFilters = useSelector(selectUserFilters);
  const usersPagination = useSelector(selectUsersPagination);

  // Local state
  const [isLoading, setIsLoading] = useState(false);

  const isSmallScreen = useMediaQuery({ maxWidth: 825 });

  // Load data on component mount and when timeframe changes
  useEffect(() => {
    dispatch(getAllUsers(userFilters));
  }, [dispatch, userFilters]);

  const refreshData = async () => {
    setIsLoading(true);
    try {
      await dispatch(getAllUsers(userFilters)).unwrap();

      toast.success("Podaci su osveženi!");
    } catch (error) {
      toast.error("Greška pri osvežavanju podataka");
    } finally {
      setIsLoading(false);
    }
  };

  const exportUsers = async () => {
    try {
      toast.info("Priprema izvoza korisnika...");

      // Fetch all user emails for export
      const allEmailsResponse = await adminService.getAllUserEmails();
      const emails = allEmailsResponse.emails || [];

      if (emails.length === 0) {
        toast.error("Nema korisnika za izvoz");
        return;
      }

      // Prepare data for Excel - just emails in a single column
      const exportData = emails.map((email) => [email]);

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([["Email"], ...exportData]);

      // Set column width
      ws["!cols"] = [{ wch: 30 }];

      XLSX.utils.book_append_sheet(wb, ws, "Korisnici");

      // Generate filename with current date
      const now = new Date();
      const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD
      const filename = `korisnici-${dateStr}.xlsx`;

      // Download the file
      XLSX.writeFile(wb, filename);

      toast.success(`Izvezeno ${emails.length} korisnika u ${filename}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Greška pri izvozu korisnika");
    }
  };

  // Debounced search to avoid too many API calls
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Clear pending debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, [searchTimeout]);

  const handleSearchChange = (value) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      dispatch(setUserFilters({ search: value, page: 1 }));
    }, 500); // Wait 500ms after user stops typing

    setSearchTimeout(timeout);
  };

  const handlePageChange = (newPage) => {
    dispatch(setUserFilters({ page: newPage }));
  };

  const handleUserAction = (action, user) => {
    switch (action) {
      case "view":
        navigate(`/admin-panel/korisnici/${user.id}`);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <SectionHeader>
        <SectionTitle>Upravljanje Korisnicima</SectionTitle>
        <SectionActions>
          <Button $secondary onClick={exportUsers}>
            <FaDownload />
            Izvezi sve
          </Button>
          <Button $secondary onClick={refreshData} disabled={isLoading}>
            <FaSync />
            Osveži
          </Button>
        </SectionActions>
      </SectionHeader>

      <Filters>
        <FilterInput
          placeholder="Pretražite po imenu ili email-u..."
          defaultValue={userFilters.search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </Filters>

      <DataTableScroll>
        <DataTable>
          <TableHeader>Korisnik</TableHeader>
          <TableHeader>Email</TableHeader>
          <TableHeader>Registracija</TableHeader>
          <TableHeader $align="center">Kursevi</TableHeader>
          <TableHeader $align="right">Potrošeno</TableHeader>
          {usersLoading ? (
            <LoadingCell>
              <l-line-spinner
                size="24"
                stroke="3"
                speed="1"
                color="var(--accent)"
              />
            </LoadingCell>
          ) : users.length > 0 ? (
            users.map((user, index) => (
              <TableRow
                key={user.id}
                $index={index}
                onClick={() => handleUserAction("view", user)}
              >
                <TableCell>
                  {user?.firstName?.trim() && user?.lastName?.trim() ? (
                    <UserInfo>
                      <UserAvatarStyled $active={user.status === "active"}>
                        {user?.firstName?.[0]}
                        {user?.lastName?.[0]}
                      </UserAvatarStyled>
                      <div>
                        <UserName>
                          {user?.firstName} {user?.lastName}
                        </UserName>
                        <UserId>ID: #{user.id.slice(0, 8)}</UserId>
                      </div>
                    </UserInfo>
                  ) : (
                    <UserInfo>
                      <UserAvatarStyled $active={user.status === "active"}>
                        {user?.email?.slice(0, 2)}
                      </UserAvatarStyled>
                      <div>
                        <UserName>Nepoznat Korisnik</UserName>
                        <UserId>ID: #{user.id.slice(0, 8)}</UserId>
                      </div>
                    </UserInfo>
                  )}
                </TableCell>
                <EmailCell>{user.email}</EmailCell>
                <DateCell>
                  {new Date(user.createdAt).toLocaleDateString("sr-latn-RS")}
                </DateCell>
                <CourseCountCell $hasContent={user.totalCourses > 0}>
                  {user.totalCourses || 0}
                </CourseCountCell>
                <SpendingCell $hasSpending={parseFloat(user.totalSpent) > 0}>
                  {priceFormatter(user.totalSpent)} RSD
                </SpendingCell>
              </TableRow>
            ))
          ) : (
            <EmptyCell>Nema korisnika za prikaz</EmptyCell>
          )}
        </DataTable>
      </DataTableScroll>

      {usersPagination && usersPagination.totalPages > 1 && (
        <PaginationContainer>
          <PaginationInfo>
            Prikazuje se{" "}
            <strong>
              {(usersPagination.currentPage - 1) * userFilters.limit + 1}
            </strong>{" "}
            -{" "}
            <strong>
              {Math.min(
                usersPagination.currentPage * userFilters.limit,
                usersPagination.totalUsers,
              )}
            </strong>{" "}
            od <strong>{usersPagination.totalUsers}</strong> korisnika
          </PaginationInfo>
          <PaginationControls>
            {isSmallScreen ? (
              <PaginationButton
                $secondary
                $disabled={!usersPagination.hasPrevPage}
                onClick={() =>
                  handlePageChange(usersPagination.currentPage - 1)
                }
              >
                ←
              </PaginationButton>
            ) : (
              <PaginationButton
                $secondary
                $disabled={!usersPagination.hasPrevPage}
                onClick={() =>
                  handlePageChange(usersPagination.currentPage - 1)
                }
              >
                ← Prethodna
              </PaginationButton>
            )}
            <PageNumbers>
              {[...Array(Math.min(5, usersPagination.totalPages))].map(
                (_, index) => {
                  let pageNum;
                  if (usersPagination.totalPages <= 5) {
                    pageNum = index + 1;
                  } else if (usersPagination.currentPage <= 3) {
                    pageNum = index + 1;
                  } else if (
                    usersPagination.currentPage >=
                    usersPagination.totalPages - 2
                  ) {
                    pageNum = usersPagination.totalPages - 4 + index;
                  } else {
                    pageNum = usersPagination.currentPage - 2 + index;
                  }

                  return (
                    <PageNumber
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      $active={pageNum === usersPagination.currentPage}
                    >
                      {pageNum}
                    </PageNumber>
                  );
                },
              )}
            </PageNumbers>
            {isSmallScreen ? (
              <PaginationButton
                $secondary
                $disabled={!usersPagination.hasNextPage}
                onClick={() =>
                  handlePageChange(usersPagination.currentPage + 1)
                }
              >
                →
              </PaginationButton>
            ) : (
              <PaginationButton
                $secondary
                $disabled={!usersPagination.hasNextPage}
                onClick={() =>
                  handlePageChange(usersPagination.currentPage + 1)
                }
              >
                Sledeća →
              </PaginationButton>
            )}
          </PaginationControls>
        </PaginationContainer>
      )}
    </>
  );
};

export default AdminUsers;
